/**
 * Receptionist Knowledge Base — pure UI logic helpers.
 *
 * Everything here is framework-free and side-effect-free so it can be unit-tested
 * without Vue/Vuex/axios (see cypress/node-tests/receptionist-kb-curation.test.js).
 * The store actions (src/store/modules/botAdmin.js) and the Knowledge tab
 * (src/pages/Calendar/admin/Knowledge/KnowledgeBase.vue) both import from here so
 * the grouping / sorting / selection / payload rules can never drift.
 *
 * BACKEND CONTRACT (teamio-bot lib/kb/*):
 *   - receptionist-kb/list returns { items: [...] } (NOT { rows }). Each item:
 *       id, type('fact'|'faq'), category, question, answer,
 *       status('draft'|'needs_review'|'approved'), source, source_ref,
 *       sensitive (0/1), enabled (0/1), updated_at, ...
 *   - upsert REJECTS `status`/`enabled` in row (mass-assignment guard) — we MUST
 *     never send them. New rows are created as `draft` and approved separately.
 *   - approve takes { ids:[int] }; set-enabled { id:int, enabled:bool }.
 *   - any handler 403s if the site isn't AI_KB_ENABLED or the caller isn't a
 *     site admin — the UI maps that single status to a friendly "not enabled" state.
 */

// The fixed display order for category groups. Anything the backend sends that is
// not in this list is bucketed under 'other' (kept last) so a new server-side
// category never silently disappears from the UI.
export const KB_CATEGORY_ORDER = [
  'hours',
  'location',
  'insurance',
  'services',
  'providers',
  'policies',
  'faq',
  'other',
];

export const KB_CATEGORY_LABELS = {
  hours: 'Hours',
  location: 'Location',
  insurance: 'Insurance',
  services: 'Services',
  providers: 'Providers',
  policies: 'Policies',
  faq: 'FAQ',
  other: 'Other',
};

// status -> badge presentation (label + a class suffix the component styles).
// draft=grey, needs_review=amber, approved=green.
export const KB_STATUS_BADGES = {
  draft: { label: 'Draft', variant: 'draft' },
  needs_review: { label: 'Needs your review', variant: 'needs-review' },
  approved: { label: 'Approved', variant: 'approved' },
};

// source -> short human chip label. Mirrors the receptionist_kb `source` ENUM.
export const KB_SOURCE_CHIPS = {
  od_seed: 'From Open Dental',
  od_inferred: 'Inferred',
  scrape: 'Website',
  doc: 'Document',
  manual: 'Added by you',
};

// The "unknown" fallback behaviors (kbGate.KB_UNKNOWN_DEFAULTS). default 'message'.
export const KB_UNKNOWN_DEFAULT_OPTIONS = [
  { value: 'transfer', label: 'Warm transfer to the front desk' },
  { value: 'message', label: 'Take a message' },
  { value: 'callback', label: 'Promise a call back' },
];

// Safe accessors — return a sensible fallback for an unrecognized status/source
// so a future enum value the backend adds never renders blank in the UI.
const KB_STATUS_BADGE_FALLBACK = { label: 'Draft', variant: 'draft' };

export function statusBadgeFor(status) {
  return KB_STATUS_BADGES[status] || KB_STATUS_BADGE_FALLBACK;
}

export function sourceChipFor(source) {
  return KB_SOURCE_CHIPS[source] || 'Source';
}

/**
 * Normalize a raw list item from the backend into the shape the UI binds to.
 * Coerces the 0/1 tinyint columns (`sensitive`, `enabled`) to real booleans and
 * buckets an unknown category under 'other'. Pure — returns a new object.
 */
export function normalizeKbRow(item) {
  const i = item || {};
  const category = KB_CATEGORY_ORDER.includes(i.category) ? i.category : 'other';
  return {
    id: i.id,
    type: i.type || 'fact',
    category,
    rawCategory: i.category, // keep what the server actually stored
    question: i.question || '',
    answer: i.answer || '',
    status: i.status || 'draft',
    source: i.source || 'manual',
    source_ref: i.source_ref || null,
    // tinyint(1) -> boolean. `== 1` handles 1 / '1' / true uniformly.
    sensitive: i.sensitive === true || i.sensitive === 1 || i.sensitive === '1',
    enabled: i.enabled === true || i.enabled === 1 || i.enabled === '1',
    updated_at: i.updated_at || null,
  };
}

// Status sort weight: needs_review floats to the TOP, then draft, then approved.
const STATUS_WEIGHT = { needs_review: 0, draft: 1, approved: 2 };

/**
 * Order rows WITHIN a category group: needs-review first, then draft, then
 * approved; ties broken by id ascending for a stable order. Returns a new array.
 */
export function sortRowsNeedsReviewFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const wa = STATUS_WEIGHT[a.status] === undefined ? 3 : STATUS_WEIGHT[a.status];
    const wb = STATUS_WEIGHT[b.status] === undefined ? 3 : STATUS_WEIGHT[b.status];
    if (wa !== wb) return wa - wb;
    return (a.id || 0) - (b.id || 0);
  });
}

/**
 * Group normalized rows by category in KB_CATEGORY_ORDER, each group's rows
 * sorted needs-review-first. Empty categories are omitted. Returns:
 *   [{ key, label, rows, needsReviewCount }]
 * with groups that CONTAIN a needs_review row floated above groups that don't,
 * so the practice sees what needs attention first at BOTH the group and row level.
 */
export function groupKbRows(rows) {
  const normalized = (rows || []).map(normalizeKbRow);
  const byCat = {};
  for (const row of normalized) {
    if (!byCat[row.category]) byCat[row.category] = [];
    byCat[row.category].push(row);
  }

  const groups = KB_CATEGORY_ORDER
    .filter((key) => byCat[key] && byCat[key].length)
    .map((key) => {
      const sorted = sortRowsNeedsReviewFirst(byCat[key]);
      return {
        key,
        label: KB_CATEGORY_LABELS[key] || key,
        rows: sorted,
        needsReviewCount: sorted.filter((r) => r.status === 'needs_review').length,
      };
    });

  // Groups with pending review float above settled groups (stable within each
  // bucket so the KB_CATEGORY_ORDER is otherwise preserved).
  return groups.sort((a, b) => {
    const aPending = a.needsReviewCount > 0 ? 0 : 1;
    const bPending = b.needsReviewCount > 0 ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending;
    return KB_CATEGORY_ORDER.indexOf(a.key) - KB_CATEGORY_ORDER.indexOf(b.key);
  });
}

/**
 * The ids of every "high-confidence" row that is still unapproved — what the
 * "Approve all high-confidence" button approves. High-confidence == a `draft`
 * row OR an `od_seed`-sourced row (the Open-Dental-seeded facts). An already
 * `approved` row is never re-collected, and a `needs_review` row is NOT swept up
 * (those genuinely need a human look). Pure — returns an array of ids.
 */
export function collectHighConfidenceApprovalIds(rows) {
  return (rows || [])
    .map(normalizeKbRow)
    .filter((r) => r.status !== 'approved' && (r.status === 'draft' || r.source === 'od_seed'))
    .map((r) => r.id)
    .filter((id) => id !== undefined && id !== null);
}

/**
 * The ids to approve from a bulk selection. Takes the set of selected ids and the
 * full row list, and keeps only ids that (a) actually exist in the list and (b)
 * are not already approved (approving an approved row is a wasted write). Pure.
 */
export function collectBulkApprovalIds(selectedIds, rows) {
  const selected = new Set((selectedIds || []).filter((id) => id !== undefined && id !== null));
  return (rows || [])
    .map(normalizeKbRow)
    .filter((r) => selected.has(r.id) && r.status !== 'approved')
    .map((r) => r.id);
}

/**
 * Build the `row` object sent to receptionist-kb/upsert.
 *
 * CRITICAL (backend mass-assignment guard): `status` and `enabled` are
 * server-controlled — the upsert handler REJECTS them with a 400. This builder
 * NEVER includes them, no matter what the caller passes. Status changes go through
 * approve; enable/disable goes through set-enabled.
 *
 * For an existing row, pass its `id` (the backend keeps status as-is on edit). For
 * a new row, omit `id`; the backend creates it as `draft` with source 'manual'.
 */
export function buildUpsertRow({ id, type, category, question, answer, source, sensitive } = {}) {
  const row = {
    type: type || 'fact',
    category: category || 'other',
    answer: typeof answer === 'string' ? answer : '',
    source: source || 'manual',
    sensitive: sensitive ? 1 : 0,
  };
  // question is optional (NULL-able for `fact` rows) — only send it when present.
  if (typeof question === 'string' && question.trim() !== '') {
    row.question = question;
  }
  // id only on an edit; absence => INSERT a fresh draft row.
  if (id !== undefined && id !== null) {
    row.id = id;
  }
  // Belt-and-suspenders: even if a caller spreads a full row in, strip the two
  // server-controlled keys so they can never reach the backend.
  delete row.status;
  delete row.enabled;
  return row;
}

/**
 * Decide how the Knowledge tab should react to a list-call result. The backend
 * returns 403 for BOTH "not entitled (AI_KB_ENABLED off)" and "not a site admin";
 * either way the right UX is the friendly "not enabled yet" empty state rather
 * than a red error. Any other failure is a real error. Pure — input is the axios
 * error's HTTP status (or null/undefined on success).
 *
 * @returns {'not-enabled'|'error'|'ok'}
 */
export function decideListOutcomeFromStatus(httpStatus) {
  if (httpStatus === 403) return 'not-enabled';
  if (httpStatus && httpStatus >= 400) return 'error';
  return 'ok';
}

// ── E3: document-upload + website-import (ingest) helpers ─────────────────────
//
// The two Knowledge-tab "seed" panels: upload a PDF/Word doc, or import the
// practice's website. Both POST to the bot (receptionist-kb/ingest-doc and
// receptionist-kb/ingest-scrape), which extract FAQ candidates as `needs_review`
// rows (the existing review flow then approves them). These helpers pin the pure
// client-side validation + the result-summary copy so the panel + store + tests
// can never drift from the bot contract (teamio-bot lib/kb/ingestDoc.js +
// ingestScrape.js).

// The ONLY document types the bot's ingestDoc accepts (ingestDoc.js PDF_EXT/DOCX_EXT).
// Anything else is rejected by the bot with a friendly error, so we reject it at the
// edge first (no pointless S3 upload + round-trip for a .txt/.jpg/.xlsx).
export const KB_DOC_ACCEPT_EXTS = ['.pdf', '.docx'];
// The matching `accept` attribute for the <input type="file"> picker.
export const KB_DOC_ACCEPT_ATTR = '.pdf,.docx';

/**
 * The lowercased file extension (incl. the dot) of a filename, or '' if none.
 * Mirrors the bot's lowerExt (ingestDoc.js) so the client and server agree on
 * exactly which extension a file has.
 */
export function kbFileExt(filename) {
  const name = typeof filename === 'string' ? filename.trim().toLowerCase() : '';
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot);
}

/**
 * Validate a picked document BEFORE uploading it. Only .pdf / .docx are accepted
 * (matches the bot's ingestDoc, which rejects everything else). Pure — takes a
 * File-like object (or just `{ name }`) and returns { valid, error }. A friendly,
 * customer-facing error string (no tech jargon) when invalid.
 *
 * @param {{name?:string}} file
 * @returns {{ valid: boolean, error: string|null, ext: string }}
 */
export function validateKbDocFile(file) {
  const name = file && typeof file.name === 'string' ? file.name : '';
  if (!name.trim()) {
    return { valid: false, error: 'Please choose a file to upload.', ext: '' };
  }
  const ext = kbFileExt(name);
  if (!KB_DOC_ACCEPT_EXTS.includes(ext)) {
    return {
      valid: false,
      error: 'Please choose a PDF (.pdf) or Word (.docx) document.',
      ext,
    };
  }
  return { valid: true, error: null, ext };
}

/**
 * Validate a website URL BEFORE importing it. Must be a well-formed http(s) URL
 * (matches the bot's ingestScrape, which rejects non-http(s) schemes). We do NOT
 * replicate the bot's SSRF/private-IP guard here — that's a server-side security
 * control; the client just gives fast, friendly feedback for the common mistakes
 * (blank, typo, ftp:/file:). Returns the normalized (trimmed) url on success.
 *
 * @param {string} rawUrl
 * @returns {{ valid: boolean, error: string|null, url: string|null }}
 */
export function validateKbScrapeUrl(rawUrl) {
  const url = typeof rawUrl === 'string' ? rawUrl.trim() : '';
  if (!url) {
    return { valid: false, error: 'Please enter your website address.', url: null };
  }
  let parsed;
  try {
    parsed = new URL(url);
  } catch (_e) {
    return { valid: false, error: "That doesn't look like a valid web address. Try something like https://yourpractice.com", url: null };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { valid: false, error: 'Please enter a web address starting with http:// or https://', url: null };
  }
  return { valid: true, error: null, url };
}

/**
 * Build the args merged into the ingest-doc request body (alongside the shared
 * { siteId, employeeId } identity, added by buildKbBody in the store). Mirrors the
 * bot handler kbIngestDoc, which requires { s3Key, filename }.
 */
export function buildIngestDocArgs({ s3Key, filename } = {}) {
  return { s3Key, filename };
}

/**
 * Build the args merged into the ingest-scrape request body (alongside the shared
 * identity). Mirrors the bot handler kbIngestScrape, which requires { url }.
 */
export function buildIngestScrapeArgs({ url } = {}) {
  return { url };
}

/**
 * Friendly result toast for a finished document ingest. The bot returns
 * { candidates, chunks, filename } — we surface the candidate count (the rows the
 * practice now reviews). 0 candidates is a real outcome (a doc with no Q&A-shaped
 * content) and gets its own honest message, not a misleading "Added 0".
 *
 * @param {{candidates?:number, filename?:string}} summary
 * @returns {string}
 */
export function ingestDocSummaryMessage(summary) {
  const n = summary && Number.isFinite(summary.candidates) ? summary.candidates : 0;
  if (n <= 0) {
    return "We couldn't find any answers to review in that document. Try a different file, or add answers manually.";
  }
  return `Added ${n} answer${n === 1 ? '' : 's'} for your review.`;
}

/**
 * Friendly result toast for a finished website import. The bot returns
 * { candidates, chunks, pagesFetched, pagesSkipped }. We surface the candidate
 * count and, honestly, the two no-result cases: nothing reachable (pagesFetched 0)
 * vs. pages read but no Q&A found. A trailing note when some pages were skipped.
 *
 * @param {{candidates?:number, pagesFetched?:number, pagesSkipped?:number}} summary
 * @returns {string}
 */
export function ingestScrapeSummaryMessage(summary) {
  const s = summary || {};
  const n = Number.isFinite(s.candidates) ? s.candidates : 0;
  const fetched = Number.isFinite(s.pagesFetched) ? s.pagesFetched : 0;
  const skipped = Number.isFinite(s.pagesSkipped) ? s.pagesSkipped : 0;

  if (fetched === 0) {
    return "We couldn't read any pages from that website. Please double-check the address and try again.";
  }
  if (n <= 0) {
    return "We read your website but couldn't find any answers to review. You can add answers manually.";
  }
  let msg = `Imported ${n} answer${n === 1 ? '' : 's'} from your website for your review.`;
  if (skipped > 0) {
    msg += ` (${skipped} page${skipped === 1 ? '' : 's'} couldn't be read and ${skipped === 1 ? 'was' : 'were'} skipped.)`;
  }
  return msg;
}
